/**
 * CHAT-02a — platform_settings feature-flag reader.
 *
 * Reads the `chat_cta_enabled` flag (a JSONB boolean in platform_settings) that
 * controls whether the buyer→agent contact CTA routes to the new conversations
 * model instead of the legacy leads form.
 *
 * SAFETY DEFAULT: returns FALSE on ANY error, denied/absent read, or non-`true`
 * value. The flag is therefore opt-in — the existing leads behavior stays in
 * place unless the row is explicitly set to `true`. (platform_settings SELECT is
 * RLS-restricted to authenticated users, so anonymous reads naturally fall back
 * to FALSE, which is the safe default.)
 *
 * Read fresh on each call (no module-level cache): a contact-modal open is a
 * low-frequency event, and caching would risk a stale value across an in-page
 * sign-in (anonymous denied-read FALSE leaking into a now-authenticated user).
 *
 * CHAT-16: this is also the single shared resolution point for the host-gated
 * tester preview override (see chatPreview.ts). Resolving it here means BOTH
 * the contact CTA and the /messages surface honor the same override without
 * duplicating the logic.
 */

import { apiGet } from '../../lib/api/client';
import { getAccessToken } from '../../lib/api/authApi';
import { isChatPreviewActive } from './chatPreview';

/**
 * DB-only read of the `chat_cta_enabled` flag — i.e. WITHOUT the CHAT-16 preview
 * override. This is the value the server renders from, so SSR-/hydration-aware
 * callers (the `useChatCtaEnabled` hook) derive their FIRST render from it and
 * fold the client-only override in only post-mount, keeping the first client
 * render identical to the server HTML. Same safe default: FALSE on any error,
 * absent row, or non-`true` value.
 */
export async function isChatCtaDbEnabled(): Promise<boolean> {
  try {
    const token = getAccessToken();
    const { data, error } = await apiGet<Array<{ value: unknown }>>(
      'platform_settings',
      'select=value&key=eq.chat_cta_enabled',
      token || undefined
    );

    if (error || !data || data.length === 0) return false;
    // value is JSONB; only an explicit boolean true enables the flag.
    return data[0].value === true;
  } catch {
    return false;
  }
}

/**
 * BUG-034 — `native_google_oauth_enabled` kill switch for the native deep-link
 * Google OAuth button.
 *
 * FAIL-SAFE DEFAULT = ENABLED. Unlike `chat_cta_enabled` (opt-in, defaults
 * FALSE), this flag guards a WORKING button, so a transient hiccup must never
 * hide it: an absent row, a read error, or any value other than an explicit
 * `false` is treated as enabled. Disabling is a deliberate flip to `false`.
 *
 * Note: this button lives on the logged-out /auth page, so the read is usually
 * anonymous. platform_settings SELECT is otherwise authenticated-only, so the
 * accompanying SQL adds an `anon` SELECT policy scoped to THIS key — without it
 * the read always errors and (correctly) falls back to enabled, but the kill
 * switch could never take effect for logged-out users.
 */
export async function isNativeGoogleOAuthEnabled(): Promise<boolean> {
  try {
    const token = getAccessToken();
    const { data, error } = await apiGet<Array<{ value: unknown }>>(
      'platform_settings',
      'select=value&key=eq.native_google_oauth_enabled',
      token || undefined
    );

    // Fail-safe: any failure or missing row → ENABLED.
    if (error || !data || data.length === 0) return true;
    // Only an explicit boolean false disables the flag.
    return data[0].value !== false;
  } catch {
    return true;
  }
}

export async function isChatCtaEnabled(): Promise<boolean> {
  // CHAT-16: chat is enabled if the DB flag is true OR the local, host-gated
  // tester preview override is active. The override is honored only on Vercel
  // previews + localhost (never on production), so flipping it on a preview
  // doesn't expose the dormant chat to prod users. Behavior is unchanged: the
  // override still short-circuits the DB read here, so the shared resolution
  // point used by ContactAgentModal / ContactAgentModalSSR is byte-identical.
  if (isChatPreviewActive()) return true;
  return isChatCtaDbEnabled();
}
